import { useCallback, useEffect, useRef, useState } from 'react'
import { getStats, getTimeseries } from '~/api/stats'
import type { StatsResponse, Timeseries } from '~/api/types'

// 크로스서비스 운영 통계를 가져오는 공용 훅. 대시보드와 각 서비스 개요 탭에서 재사용한다.
export function useStats() {
    const [stats, setStats] = useState<StatsResponse | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        getStats()
            .then(setStats)
            .catch((e) => setError(e.message))
    }, [])

    return { stats, error }
}

const POLL_MS = 30_000 // 자동 새로고침 주기(실시간성). SSE 말고 폴링.

// 운영 대시보드 전용 훅 — 통계 스냅샷 + 기간 시계열을 함께 가져오고,
// 자동 새로고침(30초 폴링)·수동 새로고침·마지막 갱신 시각을 관리한다.
export function useDashboard({
    days,
    autoRefresh,
}: {
    days: number
    autoRefresh: boolean
}) {
    const [stats, setStats] = useState<StatsResponse | null>(null)
    const [timeseries, setTimeseries] = useState<Timeseries | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [lastUpdated, setLastUpdated] = useState<number | null>(null)

    // days는 ref로 보관해 setInterval 콜백이 항상 최신 기간을 읽도록 한다.
    const daysRef = useRef(days)
    daysRef.current = days

    const refresh = useCallback(async () => {
        try {
            const [s, ts] = await Promise.all([
                getStats(),
                getTimeseries(daysRef.current),
            ])
            setStats(s)
            setTimeseries(ts)
            setError(null)
            setLastUpdated(Date.now())
        } catch (e) {
            setError(e instanceof Error ? e.message : '요청이 실패했어요.')
        }
    }, [])

    // 기간 변경 시 즉시 재요청.
    useEffect(() => {
        void refresh()
    }, [refresh, days])

    // 자동 새로고침: on일 때만 30초 폴링.
    useEffect(() => {
        if (!autoRefresh) return
        const id = window.setInterval(() => void refresh(), POLL_MS)
        return () => window.clearInterval(id)
    }, [autoRefresh, refresh])

    return { stats, timeseries, error, lastUpdated, refresh }
}
